import { reloadPage } from "./reloadPage";

/**
 * BUG-745 — recovering from a failed lazy-chunk load.
 *
 * A tab opened before a deploy still references the OLD hashed chunk names;
 * the first navigation to a not-yet-loaded page then rejects its dynamic
 * `import()`. The cure is a full reload (the new index.html names the new
 * chunks), so we try exactly ONE automatic reload, guarded by a timestamp in
 * sessionStorage so a chunk that is genuinely gone cannot loop the tab.
 */

export const CHUNK_RELOAD_STORAGE_KEY = "chunk_reload_at";

/** No second automatic reload within this window after the previous one. */
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

// Messages browsers / bundlers use for a failed dynamic import:
// - Chromium: "Failed to fetch dynamically imported module: <url>"
// - Firefox:  "error loading dynamically imported module: <url>"
// - Safari:   "Importing a module script failed."
// - webpack-style / some wrappers: ChunkLoadError, "Loading chunk N failed"
// - Vite preload helper: "Unable to preload CSS for <url>"
const CHUNK_ERROR_PATTERNS: readonly RegExp[] = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /loading (css )?chunk [\w-]+ failed/i,
  /unable to preload css/i,
];

function messageOf(error: unknown): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "message" in error) {
    const { message } = error as { message: unknown };
    return typeof message === "string" ? message : "";
  }
  return "";
}

/** True when `error` looks like a failed lazy-chunk / module-script load. */
export function isChunkLoadError(error: unknown): boolean {
  if (error && typeof error === "object") {
    const name = (error as { name?: unknown }).name;
    if (name === "ChunkLoadError") return true;
    // A Vite `vite:preloadError` event carries the original error in `payload`.
    const payload = (error as { payload?: unknown }).payload;
    if (payload !== undefined && payload !== error && isChunkLoadError(payload))
      return true;
  }
  const message = messageOf(error);
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

/** True when `t` lies in [last, last + window). */
function withinWindow(t: number, last: number): boolean {
  return t - last >= 0 && t - last < CHUNK_RELOAD_WINDOW_MS;
}

/**
 * Reload the page once, unless an automatic reload already happened within
 * the last {@link CHUNK_RELOAD_WINDOW_MS}. Returns whether a reload was
 * triggered. When sessionStorage is unavailable the guard cannot be kept, so
 * NO automatic reload happens — a loop is worse than a button press.
 *
 * The window is checked against BOTH the current time and the moment this
 * document started loading (`performance.timeOrigin`). Measuring only from
 * "now" loops on a slow boot: on dev the reloaded page needed ~11 s to boot
 * and hit the same missing chunk, by which time a 10 s window had expired
 * and a second automatic reload fired. A document whose navigation began
 * within the window of the last automatic reload IS that reload's result, so
 * it never reloads automatically again, however long it took to boot.
 */
export function tryChunkReload(
  now: number = Date.now(),
  documentStart: number = performance.timeOrigin,
): boolean {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
    const last = raw === null ? NaN : Number(raw);
    if (
      Number.isFinite(last) &&
      (withinWindow(now, last) || withinWindow(documentStart, last))
    ) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
  } catch {
    return false;
  }
  reloadPage();
  return true;
}

/**
 * Reload started by the user (the «Перезагрузить» button on the chunk
 * screen). It stamps the same guard first, so the page it opens counts as a
 * fresh reload and does not fire a second, automatic one when the chunk is
 * still missing (measured live: a manual reload > 10 s after the last
 * auto-reload otherwise produced two reloads). Storage failure never blocks
 * the user's own reload.
 */
export function reloadWithGuard(now: number = Date.now()): void {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
  } catch {
    // The user asked for it; reload regardless.
  }
  reloadPage();
}

/**
 * Vite emits `vite:preloadError` on window when a chunk's preload fails.
 * When we reload, the event is cancelled so the error is not rethrown; when
 * the loop guard blocks the reload, it propagates to the lazy import and the
 * route error screen takes over.
 */
export function installPreloadErrorHandler(): () => void {
  const handler = (event: Event) => {
    if (tryChunkReload()) event.preventDefault();
  };
  window.addEventListener("vite:preloadError", handler);
  return () => window.removeEventListener("vite:preloadError", handler);
}
